import { FC, PropsWithChildren, createContext, useContext, useMemo } from 'react';
import { StoreApi, createStore, useStore } from 'zustand';
import { dateToApiFormat } from '../../helpers/dateHelper';
import { useNotificationService } from '../notification/NotificationService';
import { OptimiserJob, OptimiserStatus } from './optimiser.types';
import { useJobToNotification } from './optimiserHelper';
import { useOptimiserBatchSignalR } from './useOptimiserBatchSignalR';

export interface OptimiserStore {
  jobs: OptimiserJob[];
  upsertJob: (newJob: OptimiserJob) => void;
  completeJob: (batchId: string, status: OptimiserStatus) => void;
  errorJob: (locationId: number, startDate: Date, error: string) => void;
}

export const OptimiserContext = createContext<StoreApi<OptimiserStore> | null>(null);

export const OptimiserProvider: FC<PropsWithChildren> = ({ children }) => {
  // const { createNotification, updateNotification } = useNotificationService(state => ({
  //   createNotification: state.createNotification,
  //   updateNotification: state.updateNotification
  // }));

  // const jobToNotification = useJobToNotification();

  const store = useMemo(() => {
    return createStore<OptimiserStore>()((set, get) => ({
      jobs: [],

      upsertJob: (newJob: OptimiserJob) => {
        const jobs = get().jobs;
        const existingIndex = jobs.findIndex(job => job.batchId === newJob.batchId);

        if (existingIndex >= 0) {
          const existingJob = jobs[existingIndex];

          const updatedJob: OptimiserJob = {
            ...existingJob,
            ...newJob,
            complete: newJob.complete,
            error: undefined,
            created: newJob.created ?? existingJob.created,
            associatedNotificationId: existingJob.associatedNotificationId
          };

          set({
            jobs: [
              ...jobs.slice(0, existingIndex),
              updatedJob,
              ...jobs.slice(existingIndex + 1)
            ]
          });

          //updateNotification(jobToNotification(updatedJob));
          return;
        }

        //const jobToAdd: OptimiserJob = {
        //  ...newJob,
        //  created: newJob.created ?? new Date()
        //};
        //
        //const notification = createNotification(jobToNotification(jobToAdd));
        //jobToAdd.associatedNotificationId = notification.id;

        set({
          //jobs: [...jobs, jobToAdd]
          jobs: [
            ...jobs,
            {
              ...newJob,
              created: newJob.created ?? new Date()
            }
          ]
        });
      },
      completeJob: (batchId: string, status: OptimiserStatus) => {
        const jobs = get().jobs;
        const existingIndex = jobs.findIndex(job => job.batchId === batchId);

        if (existingIndex < 0) {
          return;
        }

        const existingJob = jobs[existingIndex];

        const updatedJob: OptimiserJob = {
          ...existingJob,
          status,
          complete: status === OptimiserStatus.FINISHED ? true : existingJob.complete
        };

        set({
          jobs: [
            ...jobs.slice(0, existingIndex),
            updatedJob,
            ...jobs.slice(existingIndex + 1)
          ]
        });

        //const notification = jobToNotification(updatedJob);
        //updateNotification(notification);
      },

      errorJob: (locationId: number, startDate: Date, error: string) => {
        const jobs = get().jobs;

        const existingIndex = jobs.findIndex(
          job =>
            job.locationIds.includes(locationId) &&
            dateToApiFormat(job.startDate) === dateToApiFormat(startDate) &&
            !job.error
        );

        if (existingIndex < 0) {
          return;
        }

        const existingJob = jobs[existingIndex];

        const updatedJob: OptimiserJob = {
          ...existingJob,
          error,
          complete: true
        };

        set({
          jobs: [
            ...jobs.slice(0, existingIndex),
            updatedJob,
            ...jobs.slice(existingIndex + 1)
          ]
        });

        //const notification = jobToNotification(updatedJob);
        //updateNotification(notification);

      }
    }));
  }, []);

  const upsertJob = useStore(store, state => state.upsertJob);

  useOptimiserBatchSignalR(upsertJob);

  return <OptimiserContext.Provider value={store}>{children}</OptimiserContext.Provider>;
};

type SelectorReturn<S extends (s: OptimiserStore) => any> = ReturnType<S>;
export function useOptimiserService<S extends (s: OptimiserStore) => any>(selector: S): SelectorReturn<S> {
  const context = useContext(OptimiserContext);
  if (!context) {
    throw new Error('useOptimiser must be used within a OptimiserProvider');
  }
  return useStore(context, selector);
}
